import type {
  DeviceCommand,
  HomeSection,
  LibraryPayload,
  PlaybackReport,
  ProfileSummary,
  VideoSummary,
} from "@kidtube/shared";
import { DeviceApiClient, DeviceApiError } from "../../apps/samsung-tizen/src/api-client";
import { YouTubePlayerBridge } from "../../apps/samsung-tizen/src/player-bridge";
import type { DeviceCredential, DevicePolicy, HomePayload, PlayerSnapshot } from "../../apps/samsung-tizen/src/types";

const API_BASE = (import.meta.env.VITE_API_BASE_URL?.trim() || window.location.origin).replace(/\/$/, "");
const CREDENTIAL_KEY = "kidtube.child.credential.v1";
const PAIRING_KEY = "kidtube.child.pairing.v1";
const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const screen = $("screen");
const nav = $("bottom-nav");
const policyOverlay = $("policy-overlay");
const api = new DeviceApiClient(API_BASE);
let credential = readJson<DeviceCredential>(CREDENTIAL_KEY);
let home: HomePayload | null = null;
let library: LibraryPayload | null = null;
let favoriteIds = new Set<string>();
let favoritesLoaded = false;
let queue: VideoSummary[] = [];
let queueIndex = -1;
let snapshot: PlayerSnapshot = { state: "IDLE", positionSeconds: 0, durationSeconds: 0 };
let commandTimer = 0;
let heartbeatTimer = 0;
let playbackTimer = 0;
let routeGeneration = 0;
let commandPollInFlight = false;
let playerControlsTimer = 0;
let scrubbingProgress = false;

type ChildPath = "/" | "/search" | "/library" | "/profile";

const CHILD_PATHS: readonly ChildPath[] = ["/", "/search", "/library", "/profile"];
const CHILD_CHIPS = ["Tất cả", "Hoạt hình", "Ca nhạc", "Học tập", "Tiếng Anh", "Khoa học"];

const player = new YouTubePlayerBridge({
  frame: $("player-frame"),
  overlay: $("player-overlay"),
  shellUrl: new URL("/player/index.html", window.location.origin).toString(),
  onSnapshot: handleSnapshot,
  onError: toast,
  onNotice: toast,
});

function readJson<T>(key: string): T | null {
  try { const value = localStorage.getItem(key); return value === null ? null : JSON.parse(value) as T; }
  catch { localStorage.removeItem(key); return null; }
}
function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[char]!);
}
function formatTime(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, "0")}`;
}
function formatScheduleMinute(minute: number): string {
  const safe = Math.min(24 * 60, Math.max(0, Math.floor(minute)));
  return `${String(Math.floor(safe / 60)).padStart(2, "0")}:${String(safe % 60).padStart(2, "0")}`;
}
function childPath(pathname = window.location.pathname): ChildPath {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return CHILD_PATHS.includes(normalized as ChildPath) ? normalized as ChildPath : "/";
}
function progressPercent(video: VideoSummary): number {
  return video.durationSeconds > 0 ? Math.min(100, Math.max(0, Math.round(((video.progressSeconds ?? 0) / video.durationSeconds) * 100))) : 0;
}
function isRecommendedTitle(title: string): boolean {
  const normalized = title.toLocaleLowerCase("vi-VN");
  return normalized.includes("đề xuất") || normalized.includes("de xuat") || normalized.includes("gợi ý") || normalized.includes("recommended");
}
function findHeroVideo(sections: HomeSection[]): VideoSummary | null {
  const recommended = sections.find((section) => section.videos.length > 0 && isRecommendedTitle(section.title));
  return (recommended ?? sections.find((section) => section.videos.length > 0))?.videos[0] ?? null;
}
function rowMatchesChip(title: string, chip: string): boolean {
  const normalized = title.toLocaleLowerCase("vi-VN");
  if (chip === "Hoạt hình") return normalized.includes("hoạt") || normalized.includes("cartoon") || normalized.includes("animation");
  if (chip === "Ca nhạc") return normalized.includes("nhạc") || normalized.includes("music") || normalized.includes("song");
  if (chip === "Học tập") return normalized.includes("học") || normalized.includes("learn") || normalized.includes("giáo dục");
  if (chip === "Tiếng Anh") return normalized.includes("anh") || normalized.includes("english");
  if (chip === "Khoa học") return normalized.includes("khoa học") || normalized.includes("science");
  return true;
}
function attachImageFallbacks(): void {
  document.querySelectorAll<HTMLImageElement>("img[data-fallback]").forEach((image) => {
    image.addEventListener("error", () => { image.classList.add("is-broken"); image.removeAttribute("src"); }, { once: true });
  });
}
function friendlyApiMessage(error: unknown): string {
  if (error instanceof DeviceApiError) {
    return error.isInvalidCredential() ? "Thiết bị cần liên kết lại." : error.message;
  }
  return "Không thể kết nối KidTube. Kiểm tra mạng rồi thử lại.";
}
function homeSkeleton(): string {
  return `<section class="hero-card hero-skeleton skeleton"></section><div class="chip-bar skeleton-chips"><span></span><span></span><span></span><span></span></div><section class="content-section"><h2>Đang tải video</h2><div class="video-row video-grid"><span class="video-card skeleton"></span><span class="video-card skeleton"></span><span class="video-card skeleton"></span><span class="video-card skeleton"></span></div></section>`;
}
function pageSkeleton(title: string): string {
  return `<header class="page-heading"><h1>${escapeHtml(title)}</h1></header><section class="content-section"><h2>Đang tải</h2><div class="video-row video-grid"><span class="video-card skeleton"></span><span class="video-card skeleton"></span><span class="video-card skeleton"></span><span class="video-card skeleton"></span></div></section>`;
}
function toast(message: string): void {
  const element = $("toast"); element.textContent = message; element.hidden = false;
  window.setTimeout(() => { element.hidden = true; }, 3000);
}
function videoCard(video: VideoSummary): string {
  const progress = progressPercent(video);
  const image = video.thumbnailUrl ? `<img src="${escapeHtml(video.thumbnailUrl)}" alt="" loading="lazy" data-fallback="true">` : "";
  const favorite = favoriteIds.has(video.videoId);
  return `<article class="video-card-shell"><button class="video-card" type="button" data-video="${escapeHtml(video.videoId)}" aria-label="Phát ${escapeHtml(video.title)}"><span class="video-thumb"><span class="thumb-fallback">KidTube</span>${image}<span class="duration-badge">${formatTime(video.durationSeconds)}</span>${progress > 0 ? `<span class="progress-bar"><span style="width:${progress}%"></span></span>` : ""}</span><span class="video-copy"><strong>${escapeHtml(video.title)}</strong><small>${escapeHtml(video.channelName)}</small></span></button><button class="favorite-action${favorite ? " active" : ""}" type="button" data-favorite="${escapeHtml(video.videoId)}" data-title="${escapeHtml(video.title)}" aria-label="${favorite ? "Bỏ khỏi" : "Thêm vào"} yêu thích: ${escapeHtml(video.title)}" aria-pressed="${favorite}">${favorite ? "♥" : "♡"}</button></article>`;
}
function distinctVideos(videos: readonly VideoSummary[]): VideoSummary[] {
  return [...new Map(videos.map((video) => [video.videoId, video])).values()];
}
function syncFavoriteButtons(): void {
  document.querySelectorAll<HTMLButtonElement>("[data-favorite]").forEach((button) => {
    const contentId = button.dataset.favorite;
    if (!contentId) return;
    const favorite = favoriteIds.has(contentId);
    const title = button.dataset.title ?? "video";
    button.classList.toggle("active", favorite);
    button.setAttribute("aria-pressed", String(favorite));
    button.setAttribute("aria-label", `${favorite ? "Bỏ khỏi" : "Thêm vào"} yêu thích: ${title}`);
    button.textContent = favorite ? "♥" : "♡";
  });
}

interface ChildVideoRow {
  id?: string;
  title: string;
  videos: VideoSummary[];
  layout: "rail" | "grid";
  totalCount?: number;
}

const HOME_PREVIEW_LIMIT = 6;
let activeChip = "Tất cả";
let currentSectionView: string | null = null;
const scrollPositions = new Map<string, number>();

function saveCurrentScroll(): void {
  const key = currentSectionView ? `section:${currentSectionView}` : childPath();
  scrollPositions.set(key, window.scrollY || document.documentElement.scrollTop || 0);
}

function restoreScroll(key: string): void {
  const pos = scrollPositions.get(key);
  if (pos !== undefined) {
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: pos, behavior: "auto" });
    });
  }
}

function renderSections(sections: HomeSection[], continuing: VideoSummary[] = []): void {
  currentSectionView = null;
  queue = [...continuing, ...sections.flatMap((section) => section.videos)];
  const validRows: ChildVideoRow[] = [
    ...(continuing.length ? [{ title: "Xem tiếp", videos: continuing, layout: "rail" as const, totalCount: continuing.length }] : []),
    ...sections
      .filter((section) => section.videos.length > 0)
      .map((section) => ({
        id: section.id,
        title: section.title,
        videos: section.videos.slice(0, HOME_PREVIEW_LIMIT),
        layout: "rail" as const,
        totalCount: section.videos.length,
      })),
  ];

  const heroVideo = findHeroVideo(sections);
  const heroProgress = heroVideo ? progressPercent(heroVideo) : 0;
  const heroImage = heroVideo?.thumbnailUrl ? `<img src="${escapeHtml(heroVideo.thumbnailUrl)}" alt="" loading="eager" data-fallback="true">` : "";
  const heroHtml = heroVideo
    ? `<button class="hero-card" type="button" data-video="${escapeHtml(heroVideo.videoId)}" aria-label="Xem ngay ${escapeHtml(heroVideo.title)}"><span class="hero-media"><span class="thumb-fallback">KidTube</span>${heroImage}</span><span class="hero-overlay"><span class="hero-tag">Đề xuất cho con</span><strong>${escapeHtml(heroVideo.title)}</strong><small>${escapeHtml(heroVideo.channelName)} · ${formatTime(heroVideo.durationSeconds)}</small>${heroProgress > 0 ? `<span class="hero-progress"><span style="width:${heroProgress}%"></span></span>` : ""}<span class="hero-cta">Xem ngay</span></span></button>`
    : "";

  const chipsHtml = `<div class="chip-bar">${CHILD_CHIPS.map((chip) => `<button class="chip ${chip === activeChip ? "active" : ""}" type="button" data-chip="${escapeHtml(chip)}">${escapeHtml(chip)}</button>`).join("")}</div>`;

  const filteredRows = activeChip === "Tất cả"
    ? validRows
    : validRows.filter((row) => row.title !== "Xem tiếp" && rowMatchesChip(row.title, activeChip));

  const sectionsHtml = filteredRows.map(
    (row) => {
      const hasMore = (row.totalCount ?? row.videos.length) > row.videos.length && row.id;
      const headerAction = hasMore
        ? `<button class="see-all-btn" type="button" data-section="${escapeHtml(row.id!)}" aria-label="Xem tất cả ${escapeHtml(row.title)}">Xem tất cả ›</button>`
        : "";
      return `<section class="content-section content-section-rail" data-section-id="${escapeHtml(row.id ?? "")}"><div class="section-header"><h2>${escapeHtml(row.title)}</h2>${headerAction}</div><div class="video-row video-rail">${row.videos.map(videoCard).join("")}</div></section>`;
    }
  ).join("");

  screen.innerHTML = `${heroHtml}${chipsHtml}${sectionsHtml || '<div class="empty-panel"><p>Chưa có video phù hợp mục này.</p></div>'}`;
  attachImageFallbacks();
  restoreScroll("/");
}

function renderCategoryPage(sectionId: string): void {
  if (!home) return;
  const section = home.sections.find((s) => s.id === sectionId);
  if (!section || section.videos.length === 0) {
    navigate("/");
    return;
  }
  saveCurrentScroll();
  currentSectionView = sectionId;
  queue = [...section.videos];
  const countLabel = `${section.videos.length} video`;
  screen.innerHTML = `
    <header class="page-heading category-heading">
      <button class="back-link-btn" type="button" data-action="back-home" aria-label="Quay lại Trang chủ">← Trang chủ</button>
      <div class="category-title-group">
        <h1>${escapeHtml(section.title)}</h1>
        <span class="category-count">${countLabel}</span>
      </div>
    </header>
    <section class="content-section category-page-section">
      <div class="video-row video-grid category-grid">
        ${section.videos.map(videoCard).join("")}
      </div>
    </section>
  `;
  attachImageFallbacks();
  window.scrollTo({ top: 0, behavior: "auto" });
  restoreScroll(`section:${sectionId}`);
}
function renderLibrary(library: LibraryPayload): void {
  const rows = [
    { title: "Yêu thích", videos: library.favorites },
    { title: "Xem tiếp", videos: library.continueWatching },
    { title: "Đã xem gần đây", videos: library.recentlyWatched },
    { title: "Ba mẹ đã thêm", videos: library.parentAdded },
  ].filter((row) => row.videos.length > 0);
  queue = distinctVideos(rows.flatMap((row) => row.videos));
  screen.innerHTML = `<header class="page-heading"><h1>Thư viện</h1><p>Video của bé trên mọi thiết bị đã liên kết.</p></header>${rows.map((row) => `<section class="content-section"><h2>${row.title}</h2><div class="video-row video-grid">${row.videos.map(videoCard).join("")}</div></section>`).join("") || '<div class="empty-panel"><p>Thư viện chưa có video.</p></div>'}`;
  attachImageFallbacks();
}
function renderProfile(profile: ProfileSummary): void {
  queue = [];
  const dailyLimit = profile.dailyLimitMinutes === null ? "Không giới hạn" : `${profile.dailyLimitMinutes} phút`;
  const remaining = profile.remainingMinutes === null ? "Không giới hạn" : `${profile.remainingMinutes} phút`;
  const schedules = profile.scheduleToday.length > 0
    ? profile.scheduleToday.map((item) => `<li>${formatScheduleMinute(item.startMinute)}–${formatScheduleMinute(item.endMinute)}</li>`).join("")
    : "<li>Cả ngày</li>";
  const status = ({ AVAILABLE: "Có thể xem", LOCKED: "Đang khóa", OUTSIDE_SCHEDULE: "Ngoài giờ xem", DAILY_LIMIT_REACHED: "Đã hết thời gian" } as const)[profile.status];
  const usagePercent = Math.round(Math.min(1, Math.max(0, profile.usageProgress)) * 100);
  screen.innerHTML = `<header class="page-heading"><h1>Của bé</h1><p>Hồ sơ chỉ xem. Ba mẹ quản lý giới hạn và lịch.</p></header><section class="profile-card"><div class="profile-identity"><span class="profile-avatar" aria-hidden="true">☺</span><div><h2>${escapeHtml(profile.name)}</h2><span class="profile-status ${profile.allowed ? "available" : "restricted"}">${status}</span></div></div>${profile.message ? `<p class="profile-message">${escapeHtml(profile.message)}</p>` : ""}<div class="usage-progress" role="progressbar" aria-label="Thời gian xem hôm nay" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${usagePercent}"><span style="width:${usagePercent}%"></span></div><div class="profile-stats"><div><strong>${profile.watchMinutesToday}</strong><span>phút hôm nay</span></div><div><strong>${remaining}</strong><span>còn lại</span></div><div><strong>${profile.watchedVideoCount}</strong><span>video đã xem</span></div><div><strong>${profile.favoritesCount}</strong><span>yêu thích</span></div></div><dl class="profile-details"><div><dt>Giới hạn ngày</dt><dd>${dailyLimit}</dd></div><div><dt>Lịch hôm nay</dt><dd><ul>${schedules}</ul></dd></div></dl></section>`;
}
function applyPolicy(policy: DevicePolicy): void {
  policyOverlay.hidden = policy.allowed;
  $("policy-message").textContent = policy.message ?? "Ba mẹ sẽ mở lại khi đến giờ nhé.";
  $("policy-usage").textContent = policy.dailyLimitMinutes === null ? "" : `Đã xem ${Math.floor(policy.usedTodaySeconds / 60)} / ${policy.dailyLimitMinutes} phút`;
  $("usage-label").textContent = policy.dailyLimitMinutes === null ? "" : `${Math.floor(policy.usedTodaySeconds / 60)}/${policy.dailyLimitMinutes} phút`;
  if (!policy.allowed && player.isOpen()) closePlayer();
}
async function loadFavoriteIds(): Promise<void> {
  if (!credential || favoritesLoaded) return;
  const favorites = await new DeviceApiClient(credential.apiBaseUrl).getFavorites(credential);
  favoriteIds = new Set(favorites.map((video) => video.videoId));
  favoritesLoaded = true;
  syncFavoriteButtons();
}
async function loadHome(generation: number): Promise<void> {
  if (!credential) return;
  if (home === null) screen.innerHTML = homeSkeleton();
  else renderSections(home.sections, home.continueWatching);
  try {
    const [nextHome] = await Promise.all([
      new DeviceApiClient(credential.apiBaseUrl).getHome(credential),
      loadFavoriteIds(),
    ]);
    if (generation !== routeGeneration || childPath() !== "/") return;
    applyPolicy(nextHome.policy);
    if (!nextHome.policy.allowed) return;
    home = nextHome;
    renderSections(nextHome.sections, nextHome.continueWatching);
    $("connection-label").textContent = "Đã kết nối";
  } catch (error) {
    if (generation === routeGeneration) handleApiError(error, () => void renderRoute("/"));
  }
}
async function loadLibrary(generation: number): Promise<void> {
  if (!credential) return;
  if (library === null) screen.innerHTML = pageSkeleton("Thư viện");
  else renderLibrary(library);
  try {
    const nextLibrary = await new DeviceApiClient(credential.apiBaseUrl).getLibrary(credential);
    if (generation !== routeGeneration || childPath() !== "/library") return;
    applyPolicy(nextLibrary.policy);
    if (!nextLibrary.policy.allowed) return;
    library = nextLibrary;
    favoriteIds = new Set(nextLibrary.favorites.map((video) => video.videoId));
    favoritesLoaded = true;
    renderLibrary(nextLibrary);
    $("connection-label").textContent = "Đã kết nối";
  } catch (error) {
    if (generation === routeGeneration) handleApiError(error, () => void renderRoute("/library"));
  }
}
async function loadProfile(generation: number): Promise<void> {
  if (!credential) return;
  screen.innerHTML = pageSkeleton("Của bé");
  try {
    const profile = await new DeviceApiClient(credential.apiBaseUrl).getProfileSummary(credential);
    if (generation !== routeGeneration || childPath() !== "/profile") return;
    renderProfile(profile);
    $("connection-label").textContent = "Đã kết nối";
  } catch (error) {
    if (generation === routeGeneration) handleApiError(error, () => void renderRoute("/profile"));
  }
}
function openVideo(videoId: string): void {
  const index = queue.findIndex((video) => video.videoId === videoId);
  if (index >= 0) openQueueIndex(index);
}
function openQueueIndex(index: number): void {
  const video = queue[index];
  if (!video) return;
  if (player.isOpen()) void reportPlayback();
  queueIndex = index;
  resetPlayerUi();
  player.open(video);
  updatePlayerCopy();
}
function updatePlayerCopy(): void {
  const video = queue[queueIndex];
  if (!video) return;
  $("info-channel").textContent = video.channelName;
  $("info-title").textContent = video.title;
  $("info-banner").hidden = false;
  $("info-next").textContent = queue[queueIndex + 1]?.title ? `Tiếp: ${queue[queueIndex + 1]!.title}` : "Video cuối";
  $("player-end-title").textContent = video.title;
  renderEpisodeList();
}
function moveQueue(delta: number): void {
  const next = queueIndex + delta;
  if (next >= 0 && next < queue.length) openQueueIndex(next);
}
function playerPosition(value: number): number {
  const maximum = snapshot.durationSeconds > 0 ? snapshot.durationSeconds : 86_400;
  return Math.max(0, Math.min(maximum, value));
}
function setProgressDisplay(positionSeconds: number, durationSeconds: number, bufferedFraction = 0): void {
  const duration = Math.max(0, durationSeconds);
  const position = Math.max(0, Math.min(duration || 1, positionSeconds));
  const percentage = duration > 0 ? (position / duration) * 100 : 0;
  const buffered = Math.max(0, Math.min(100, bufferedFraction * 100));
  const progress = $("player-progress") as HTMLInputElement;
  progress.max = String(Math.max(1, duration));
  progress.value = String(position);
  $("player-played").style.width = `${percentage}%`;
  $("player-buffered").style.width = `${buffered}%`;
  $("player-current").textContent = formatTime(position);
  $("player-duration").textContent = formatTime(duration);
}
function formatPlaybackRate(rate: number): string {
  return `${Number.isInteger(rate) ? rate : String(rate)}×`;
}
function availablePlaybackRates(): number[] {
  return (snapshot.availablePlaybackRates ?? []).filter((rate) => Number.isFinite(rate) && rate > 0);
}
function renderPlaybackOptions(): void {
  const rates = availablePlaybackRates();
  const currentRate = snapshot.playbackRate ?? 1;
  const settings = $("player-settings") as HTMLButtonElement;
  settings.textContent = formatPlaybackRate(currentRate);
  settings.disabled = rates.length < 2;
  $("player-speed-options").innerHTML = rates.map((rate) => `<button type="button" data-playback-rate="${rate}" aria-pressed="${rate === currentRate}" class="${rate === currentRate ? "active" : ""}">${formatPlaybackRate(rate)}</button>`).join("");
}
function updatePlayerCapabilities(next: PlayerSnapshot): void {
  const captions = $("player-cc") as HTMLButtonElement;
  const captionsAvailable = next.captionsAvailable === true;
  const captionsEnabled = next.captionsEnabled === true;
  captions.disabled = !captionsAvailable;
  captions.classList.toggle("active", captionsEnabled);
  captions.setAttribute("aria-pressed", String(captionsEnabled));
  captions.setAttribute("aria-label", captionsAvailable ? (captionsEnabled ? "Tắt phụ đề" : "Bật phụ đề") : "Phụ đề không khả dụng");
  renderPlaybackOptions();
}
function renderEpisodeList(): void {
  $("player-episode-list").innerHTML = queue.map((video, index) => `<button type="button" data-queue-index="${index}" class="${index === queueIndex ? "active" : ""}" aria-current="${index === queueIndex ? "true" : "false"}"><span>${index + 1}</span><strong>${escapeHtml(video.title)}</strong><small>${escapeHtml(video.channelName)}</small></button>`).join("");
  const episodes = $("player-episodes") as HTMLButtonElement;
  episodes.disabled = queue.length < 2;
}
function playerPanelOpen(): boolean {
  return !$("player-settings-panel").hidden || !$("player-episodes-panel").hidden;
}
function setPlayerPanel(panel: "settings" | "episodes" | null): void {
  $("player-settings-panel").hidden = panel !== "settings";
  $("player-episodes-panel").hidden = panel !== "episodes";
  $("player-settings").setAttribute("aria-expanded", String(panel === "settings"));
  $("player-episodes").setAttribute("aria-expanded", String(panel === "episodes"));
  if (panel) showPlayerControls(false);
  else showPlayerControls();
}
function showPlayerControls(schedule = true): void {
  window.clearTimeout(playerControlsTimer);
  playerControlsTimer = 0;
  $("player-controls").classList.remove("is-hidden");
  if (schedule && player.isOpen() && snapshot.state === "PLAYING" && !playerPanelOpen() && $("player-end").hidden) {
    playerControlsTimer = window.setTimeout(hidePlayerControls, 3_000);
  }
}
function hidePlayerControls(): void {
  playerControlsTimer = 0;
  if (!player.isOpen() || snapshot.state !== "PLAYING" || scrubbingProgress || playerPanelOpen() || !$("player-end").hidden) return;
  $("player-controls").classList.add("is-hidden");
}
function showPlayerEnd(): void {
  const nextVideo = queue[queueIndex + 1];
  const next = $("player-end-next") as HTMLButtonElement;
  next.hidden = !nextVideo;
  next.textContent = nextVideo ? `Phát tiếp: ${nextVideo.title}` : "Phát video tiếp theo";
  $("player-end").hidden = false;
  setPlayerPanel(null);
  window.clearTimeout(playerControlsTimer);
  $("player-controls").classList.add("is-hidden");
}
function hidePlayerEnd(): void {
  $("player-end").hidden = true;
}
function resetPlayerUi(): void {
  scrubbingProgress = false;
  hidePlayerEnd();
  setPlayerPanel(null);
  showPlayerControls(false);
}
function updateFullscreenControl(): void {
  const overlay = $("player-overlay");
  const control = $("player-fullscreen") as HTMLButtonElement;
  const supported = typeof overlay.requestFullscreen === "function" && typeof document.exitFullscreen === "function";
  const fullscreen = document.fullscreenElement === overlay;
  control.disabled = !supported;
  control.setAttribute("aria-label", fullscreen ? "Thoát toàn màn hình" : "Toàn màn hình");
}
function toggleFullscreen(): void {
  const overlay = $("player-overlay");
  if (document.fullscreenElement === overlay) {
    void document.exitFullscreen().catch(() => toast("Không thể thoát toàn màn hình."));
    return;
  }
  if (typeof overlay.requestFullscreen !== "function") {
    toast("Thiết bị này không hỗ trợ toàn màn hình.");
    return;
  }
  void overlay.requestFullscreen().catch(() => toast("Không thể bật toàn màn hình."));
}
function seekBy(seconds: number): void {
  if (!player.seekTo(playerPosition(snapshot.positionSeconds + seconds))) {
    toast("Trình phát chưa sẵn sàng để tua video.");
  }
}
function handleSnapshot(next: PlayerSnapshot): void {
  const stateChanged = snapshot.state !== next.state;
  snapshot = next;
  const toggle = $("player-toggle") as HTMLButtonElement;
  const playing = next.state === "PLAYING";
  toggle.textContent = playing ? "❚❚" : "▶";
  toggle.setAttribute("aria-label", playing ? "Tạm dừng video" : "Phát video");
  if (!scrubbingProgress) setProgressDisplay(next.positionSeconds, next.durationSeconds, next.bufferedFraction ?? 0);
  $("info-time").textContent = `${formatTime(next.positionSeconds)} / ${formatTime(next.durationSeconds)}`;
  updatePlayerCapabilities(next);
  if (next.state === "ENDED") showPlayerEnd();
  else if (stateChanged && !$("player-end").hidden) hidePlayerEnd();
  if (next.state !== "PLAYING") showPlayerControls(false);
  else if (stateChanged) showPlayerControls();
}
function closePlayer(): void {
  if (player.isOpen()) void reportPlayback();
  window.clearTimeout(playerControlsTimer);
  scrubbingProgress = false;
  hidePlayerEnd();
  setPlayerPanel(null);
  player.close();
  if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
  const key = currentSectionView ? `section:${currentSectionView}` : childPath();
  restoreScroll(key);
}
async function reportPlayback(): Promise<void> {
  if (!credential) return;
  const video = player.activeVideo();
  const report: PlaybackReport = { videoId: video?.videoId ?? null, title: video?.title ?? null, thumbnailUrl: video?.thumbnailUrl ?? null, channelId: video?.channelId ?? null, channelName: video?.channelName ?? null, positionSeconds: snapshot.positionSeconds, durationSeconds: snapshot.durationSeconds, state: snapshot.state, occurredAt: new Date().toISOString() };
  try { applyPolicy((await new DeviceApiClient(credential.apiBaseUrl).reportPlayback(credential, report)).policy); } catch (error) { handleApiError(error); }
}
async function heartbeat(): Promise<void> {
  if (!credential) return;
  try { applyPolicy((await new DeviceApiClient(credential.apiBaseUrl).heartbeat(credential)).policy); } catch (error) { handleApiError(error); }
}
async function pollCommands(): Promise<void> {
  if (!credential || commandPollInFlight) return;
  commandPollInFlight = true;
  const activeCredential = credential;
  const client = new DeviceApiClient(activeCredential.apiBaseUrl);
  try {
    for (const command of await client.pollCommands(activeCredential)) {
      if (credential?.deviceId !== activeCredential.deviceId) break;
      let success = true;
      try { applyCommand(command); } catch { success = false; }
      await client.acknowledgeCommand(activeCredential, command.id, success, { applied: success });
      if (success) await afterCommandAcknowledged(command);
    }
  } catch (error) { handleApiError(error); }
  finally { commandPollInFlight = false; }
}
function invalidateContent(): void {
  home = null;
  library = null;
  favoritesLoaded = false;
  favoriteIds.clear();
}
function applyCommand(command: DeviceCommand): void {
  if (command.type === "PAUSE") player.pause();
  else if (command.type === "RESUME") player.play();
  else if (command.type === "STOP") closePlayer();
  else if (["BLOCK_VIDEO", "BLOCK_CHANNEL", "BLOCK_AND_STOP"].includes(command.type)) closePlayer();
  else if (command.type === "LOCK") { routeGeneration += 1; policyOverlay.hidden = false; closePlayer(); }
}
async function afterCommandAcknowledged(command: DeviceCommand): Promise<void> {
  if (command.type === "LOCK" || command.type === "UNLOCK") {
    await heartbeat();
  }
  if (command.type === "UNLOCK") {
    await waitForUnlockReady();
    return;
  }
  if (["REFRESH", "BLOCK_VIDEO", "BLOCK_CHANNEL", "BLOCK_AND_STOP"].includes(command.type)) {
    invalidateContent();
    await renderRoute(childPath());
  }
}

// ponytail: max 10s polling; if backend never flips policy.allowed, fall back to refresh anyway.
async function waitForUnlockReady(attempts = 10): Promise<void> {
  for (let i = 0; i < attempts; i += 1) {
    const policy = await pollPolicyOnce();
    if (policy?.allowed) break;
    await new Promise((resolve) => setTimeout(resolve, 1000));
  }
  invalidateContent();
  await renderRoute(childPath());
}

async function pollPolicyOnce(): Promise<DevicePolicy | null> {
  if (!credential) return null;
  try {
    const policy = (await new DeviceApiClient(credential.apiBaseUrl).heartbeat(credential)).policy;
    applyPolicy(policy);
    return policy;
  } catch {
    return null;
  }
}
function handleApiError(error: unknown, retry?: () => void): void {
  if (error instanceof DeviceApiError && error.isInvalidCredential()) { resetCredential(); return; }
  $("connection-label").textContent = "Ngoại tuyến";
  if (retry) screen.innerHTML = `<div class="error-panel"><p>${escapeHtml(friendlyApiMessage(error))}</p><button class="primary-action" id="retry" type="button">Thử lại</button></div>`, $("retry").onclick = retry;
}
function resetCredential(): void {
  credential = null; localStorage.removeItem(CREDENTIAL_KEY); stopLoops(); void beginPairing(true);
}
async function beginPairing(force = false): Promise<void> {
  nav.hidden = true; policyOverlay.hidden = true;
  if (force) localStorage.removeItem(PAIRING_KEY);
  try {
    const pending = readJson<any>(PAIRING_KEY) ?? await api.startPairing({ platform: "WEB_IOS", deviceKind: "WEB_CHILD", model: navigator.userAgent.slice(0, 120), osVersion: navigator.platform || "iOS Web", appVersion: "0.1.0" });
    localStorage.setItem(PAIRING_KEY, JSON.stringify(pending));
    screen.innerHTML = `<section class="pairing-card"><h1>Liên kết với ba mẹ</h1><p>Nhập mã này trong Parent Web.</p><div class="pairing-code">${pending.code}</div><p id="pair-status">Đang chờ xác nhận…</p></section>`;
    window.setTimeout(() => void pollPairing(pending), pending.pollAfterSeconds * 1000);
  } catch (error) { handleApiError(error, () => void beginPairing(true)); }
}
async function pollPairing(pending: any): Promise<void> {
  try {
    const status = await api.pairingStatus(pending.pairingId, pending.pollingSecret);
    if (status.status === "PENDING") { window.setTimeout(() => void pollPairing(pending), pending.pollAfterSeconds * 1000); return; }
    if (status.status !== "PAIRED" || !status.deviceId || !status.deviceToken || !status.apiBaseUrl) { void beginPairing(true); return; }
    credential = { deviceId: status.deviceId, deviceToken: status.deviceToken, apiBaseUrl: status.apiBaseUrl };
    localStorage.setItem(CREDENTIAL_KEY, JSON.stringify(credential)); localStorage.removeItem(PAIRING_KEY); await activate();
  } catch (error) { handleApiError(error, () => void beginPairing(true)); }
}
function startLoops(): void {
  stopLoops(); heartbeatTimer = window.setInterval(() => void heartbeat(), 20_000); commandTimer = window.setInterval(() => void pollCommands(), 4_000); playbackTimer = window.setInterval(() => void reportPlayback(), 15_000);
}
function stopLoops(): void { clearInterval(heartbeatTimer); clearInterval(commandTimer); clearInterval(playbackTimer); }
async function activate(): Promise<void> {
  nav.hidden = false;
  startLoops();
  const path = childPath();
  if (window.location.pathname !== path) window.history.replaceState({}, "", path);
  await Promise.all([renderRoute(path), heartbeat(), pollCommands()]);
}

async function toggleFavorite(contentId: string, button: HTMLButtonElement): Promise<void> {
  if (!credential || button.disabled) return;
  button.disabled = true;
  try {
    await loadFavoriteIds();
    const client = new DeviceApiClient(credential.apiBaseUrl);
    const removing = favoriteIds.has(contentId);
    if (removing) await client.removeFavorite(credential, contentId);
    else await client.addFavorite(credential, contentId);
    if (removing) favoriteIds.delete(contentId); else favoriteIds.add(contentId);
    syncFavoriteButtons();
    toast(removing ? "Đã bỏ khỏi yêu thích" : "Đã thêm vào yêu thích");
    if (childPath() === "/library") void renderRoute("/library");
  } catch (error) {
    handleApiError(error);
    toast(friendlyApiMessage(error));
  } finally {
    button.disabled = false;
  }
}
function openSearchScreen(generation: number): void {
  screen.innerHTML = `<header class="page-heading"><h1>Tìm kiếm</h1><p>Không có Shorts, bình luận hoặc đề xuất YouTube.</p></header><form id="search-form" class="search-form"><input id="search-input" minlength="2" maxlength="120" required aria-label="Từ khóa" placeholder="Nhập tên video"><button class="primary-action" type="submit">Tìm</button></form><div id="search-status" class="search-status" role="status"></div><div id="search-results"></div><button id="search-more" class="secondary-action search-more" type="button" hidden>Tải thêm</button>`;
  let items: VideoSummary[] = [];
  let nextCursor: string | null = null;
  const input = $("search-input") as HTMLInputElement;
  const results = $("search-results");
  const status = $("search-status");
  const more = $("search-more") as HTMLButtonElement;
  const renderSearchResults = (): void => {
    results.innerHTML = items.length > 0 ? `<div class="video-row video-grid search-grid">${items.map(videoCard).join("")}</div>` : `<div class="empty-panel"><p>Không tìm thấy video phù hợp.</p></div>`;
    attachImageFallbacks();
    more.hidden = nextCursor === null;
    more.disabled = false;
  };
  const runSearch = async (append: boolean): Promise<void> => {
    if (!credential) return;
    const query = input.value.trim();
    if (query.length < 2) return;
    if (!append) { items = []; nextCursor = null; results.innerHTML = homeSkeleton(); }
    status.textContent = append ? "Đang tải thêm…" : "Đang tìm…";
    more.disabled = true;
    try {
      const [result] = await Promise.all([
        new DeviceApiClient(credential.apiBaseUrl).search(credential, query, append ? nextCursor : null),
        loadFavoriteIds(),
      ]);
      if (generation !== routeGeneration || childPath() !== "/search") return;
      applyPolicy(result.policy);
      items = append ? distinctVideos([...items, ...result.items]) : result.items;
      nextCursor = result.nextCursor;
      queue = items;
      status.textContent = "";
      renderSearchResults();
    } catch (error) {
      if (generation !== routeGeneration) return;
      if (error instanceof DeviceApiError && error.isInvalidCredential()) { handleApiError(error); return; }
      status.textContent = friendlyApiMessage(error);
      more.disabled = false;
    }
  };
  $("search-form").addEventListener("submit", (event) => { event.preventDefault(); void runSearch(false); });
  more.onclick = () => void runSearch(true);
}
function updateNavActive(path: ChildPath): void {
  const activeId = ({ "/": "nav-home", "/search": "nav-search", "/library": "nav-library", "/profile": "nav-profile" } as const)[path];
  ["nav-home", "nav-search", "nav-library", "nav-profile"].forEach((id) => {
    const item = $(id);
    const active = id === activeId;
    item.classList.toggle("active", active);
    if (active) item.setAttribute("aria-current", "page"); else item.removeAttribute("aria-current");
  });
}
async function renderRoute(path: ChildPath): Promise<void> {
  const generation = ++routeGeneration;
  updateNavActive(path);
  if (path === "/") await loadHome(generation);
  else if (path === "/search") openSearchScreen(generation);
  else if (path === "/library") await loadLibrary(generation);
  else await loadProfile(generation);
}
function navigate(path: ChildPath, replace = false): void {
  if (window.location.pathname !== path) window.history[replace ? "replaceState" : "pushState"]({}, "", path);
  window.scrollTo({ top: 0, behavior: "auto" });
  void renderRoute(path);
}

screen.addEventListener("click", (event) => {
  const target = event.target as Element;
  const backHome = target.closest<HTMLElement>('[data-action="back-home"]');
  if (backHome) {
    if (home) renderSections(home.sections, home.continueWatching);
    return;
  }
  const seeAll = target.closest<HTMLElement>("[data-section]");
  if (seeAll?.dataset.section) {
    saveCurrentScroll();
    renderCategoryPage(seeAll.dataset.section);
    return;
  }
  const favorite = target.closest<HTMLButtonElement>("[data-favorite]");
  if (favorite?.dataset.favorite) { void toggleFavorite(favorite.dataset.favorite, favorite); return; }
  const card = target.closest<HTMLElement>("[data-video]");
  if (card?.dataset.video) {
    saveCurrentScroll();
    openVideo(card.dataset.video);
    return;
  }
  const chipBtn = target.closest<HTMLElement>("[data-chip]");
  if (chipBtn?.dataset.chip) {
    activeChip = chipBtn.dataset.chip;
    if (home && childPath() === "/") renderSections(home.sections, home.continueWatching);
  }
});
$("nav-home").onclick = () => navigate("/");
$("nav-search").onclick = () => navigate("/search");
$("nav-library").onclick = () => navigate("/library");
$("nav-profile").onclick = () => navigate("/profile");
$("brand-home").onclick = () => navigate("/");
window.addEventListener("popstate", () => void renderRoute(childPath()));
$("player-previous").onclick = () => { showPlayerControls(); moveQueue(-1); };
$("player-next").onclick = () => { showPlayerControls(); moveQueue(1); };
$("player-rewind").onclick = () => { showPlayerControls(); seekBy(-10); };
$("player-forward").onclick = () => { showPlayerControls(); seekBy(10); };
$("player-toggle").onclick = () => { showPlayerControls(); snapshot.state === "PLAYING" ? player.pause() : player.play(); };
$("player-exit").onclick = closePlayer;
$("player-cc").onclick = () => { showPlayerControls(); if (!player.setCaptions(snapshot.captionsEnabled !== true)) toast("Trình phát chưa sẵn sàng để đổi phụ đề."); };
$("player-settings").onclick = () => { renderPlaybackOptions(); setPlayerPanel($("player-settings-panel").hidden ? "settings" : null); };
$("player-episodes").onclick = () => { renderEpisodeList(); setPlayerPanel($("player-episodes-panel").hidden ? "episodes" : null); };
$("player-fullscreen").onclick = toggleFullscreen;
$("player-end-next").onclick = () => moveQueue(1);
$("player-replay").onclick = () => { hidePlayerEnd(); showPlayerControls(); player.seekTo(0); player.play(); };
$("player-home").onclick = () => { closePlayer(); navigate("/"); };
const progress = $("player-progress") as HTMLInputElement;
progress.addEventListener("pointerdown", () => { scrubbingProgress = true; showPlayerControls(false); });
progress.addEventListener("input", () => setProgressDisplay(Number(progress.value), snapshot.durationSeconds, snapshot.bufferedFraction ?? 0));
progress.addEventListener("change", () => { const position = Number(progress.value); scrubbingProgress = false; if (!player.seekTo(position)) toast("Trình phát chưa sẵn sàng để tua video."); showPlayerControls(); });
$("player-interaction").addEventListener("click", () => { if ($("player-controls").classList.contains("is-hidden")) showPlayerControls(); else hidePlayerControls(); });
$("player-overlay").addEventListener("pointermove", () => showPlayerControls());
$("player-controls").addEventListener("focusin", () => showPlayerControls(false));
$("player-controls").addEventListener("focusout", () => window.setTimeout(() => showPlayerControls(), 0));
document.querySelectorAll<HTMLButtonElement>("[data-close-player-panel]").forEach((button) => { button.onclick = () => setPlayerPanel(null); });
$("player-speed-options").addEventListener("click", (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const button = target.closest<HTMLButtonElement>("[data-playback-rate]");
  const rate = Number(button?.dataset.playbackRate);
  if (!Number.isFinite(rate)) return;
  if (!player.setPlaybackRate(rate)) toast("Trình phát chưa sẵn sàng để đổi tốc độ.");
  setPlayerPanel(null);
});
$("player-episode-list").addEventListener("click", (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const button = target.closest<HTMLButtonElement>("[data-queue-index]");
  const index = Number(button?.dataset.queueIndex);
  if (Number.isInteger(index)) openQueueIndex(index);
});
document.addEventListener("fullscreenchange", updateFullscreenControl);
document.addEventListener("keydown", (event) => {
  if (!player.isOpen()) return;
  if (event.key === "Escape") {
    if (playerPanelOpen()) setPlayerPanel(null);
    else if (document.fullscreenElement) void document.exitFullscreen();
    else closePlayer();
    return;
  }
  if (event.target instanceof HTMLInputElement || event.target instanceof HTMLButtonElement) return;
  if (event.key === " " || event.key === "Spacebar") { event.preventDefault(); snapshot.state === "PLAYING" ? player.pause() : player.play(); showPlayerControls(); }
  else if (event.key === "ArrowLeft") { event.preventDefault(); seekBy(-10); showPlayerControls(); }
  else if (event.key === "ArrowRight") { event.preventDefault(); seekBy(10); showPlayerControls(); }
});
updateFullscreenControl();
document.addEventListener("visibilitychange", () => { if (document.hidden) void reportPlayback(); else { void heartbeat(); void pollCommands(); } });
window.addEventListener("beforeunload", () => { void reportPlayback(); player.destroy(); stopLoops(); });
if ("serviceWorker" in navigator) void navigator.serviceWorker.register("/service-worker.js");
$("app").setAttribute("aria-busy", "false");
credential ? void activate() : void beginPairing();
